import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { Livestream, Profile, Membership } from './types';

// Get environment variables from window (injected by HTML)
declare global {
  interface Window {
    SUPABASE_URL: string;
    SUPABASE_ANON_KEY: string;
  }
}

let supabaseClient: SupabaseClient | null = null;

/**
 * Get or create Supabase client instance (singleton)
 */
export function getSupabase(): SupabaseClient {
  if (!supabaseClient) {
    const url = window.SUPABASE_URL;
    const key = window.SUPABASE_ANON_KEY;

    if (!url || !key) {
      throw new Error('Supabase configuration missing. Check environment variables.');
    }

    supabaseClient = createClient(url, key, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
      },
    });
  }

  return supabaseClient;
}

/**
 * Get current user's profile and membership
 */
export async function getCurrentUserData(): Promise<{
  profile: Profile;
  membership: Membership;
} | null> {
  const supabase = getSupabase();

  const { data: { user }, error: authError } = await supabase.auth.getUser();
  if (authError || !user) {
    return null;
  }

  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', user.id)
    .single();

  if (profileError || !profile) {
    return null;
  }

  const { data: membership, error: membershipError } = await supabase
    .from('memberships')
    .select('*')
    .eq('user_id', user.id)
    .single();

  if (membershipError || !membership) {
    return null;
  }

  return { profile, membership };
}

/**
 * Check if user has Friends tier access (or higher)
 */
export async function hasFriendsAccess(): Promise<boolean> {
  const userData = await getCurrentUserData();
  if (!userData) return false;

  const { membership } = userData;
  return (
    ['FRIENDS', 'COMPANION', 'MENTOR'].includes(membership.tier) &&
    membership.status === 'active'
  );
}

/**
 * Get current or upcoming livestream
 */
export async function getCurrentLivestream(): Promise<Livestream | null> {
  const supabase = getSupabase();

  // First try to get a live stream
  const { data: liveStreams, error: liveError } = await supabase
    .from('livestreams')
    .select('*')
    .eq('status', 'live')
    .order('started_at', { ascending: false })
    .limit(1);

  // Ignore PGRST116 (no rows) - this is expected when there are no live streams
  if (liveError && liveError.code !== 'PGRST116') {
    console.error('Error fetching live stream:', liveError);
  }

  if (liveStreams && liveStreams.length > 0) return liveStreams[0];

  // If no live stream, get the next scheduled one
  const { data: scheduledStreams, error: scheduledError } = await supabase
    .from('livestreams')
    .select('*')
    .eq('status', 'scheduled')
    .gte('scheduled_for', new Date().toISOString())
    .order('scheduled_for', { ascending: true })
    .limit(1);

  // Ignore PGRST116 (no rows) - this is expected when there are no scheduled streams
  if (scheduledError && scheduledError.code !== 'PGRST116') {
    console.error('Error fetching scheduled stream:', scheduledError);
  }

  return (scheduledStreams && scheduledStreams.length > 0) ? scheduledStreams[0] : null;
}

/**
 * Get archived livestreams
 */
export async function getArchivedLivestreams(): Promise<Livestream[]> {
  const supabase = getSupabase();

  const { data, error } = await supabase
    .from('livestreams')
    .select('*')
    .in('status', ['ended', 'archived'])
    .order('ended_at', { ascending: false });

  if (error) {
    console.error('Error fetching archived streams:', error);
    return [];
  }

  return data || [];
}

/**
 * Track that user viewed a livestream
 */
export async function trackLivestreamView(livestreamId: string): Promise<void> {
  const supabase = getSupabase();
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) return;

  await supabase
    .from('livestream_views')
    .upsert({
      livestream_id: livestreamId,
      user_id: user.id,
      viewed_at: new Date().toISOString(),
    }, {
      onConflict: 'livestream_id,user_id'
    });
}

/**
 * Update user notification preferences
 */
export async function updateNotificationPreferences(
  emailNotifications: boolean,
  smsEnabled: boolean,
  marketingEmails: boolean
): Promise<{ success: boolean; error?: string }> {
  const supabase = getSupabase();
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    return { success: false, error: 'Not authenticated' };
  }

  const { error } = await supabase
    .from('profiles')
    .update({
      email_notifications: emailNotifications,
      sms_enabled: smsEnabled,
      marketing_emails: marketingEmails,
    })
    .eq('id', user.id);

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * Subscribe to livestream status changes (realtime)
 */
export function subscribeLivestreamUpdates(
  callback: (livestream: Livestream) => void
): () => void {
  const supabase = getSupabase();

  const channel = supabase
    .channel('livestream-updates')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'livestreams',
      },
      (payload) => {
        callback(payload.new as Livestream);
      }
    )
    .subscribe();

  // Return unsubscribe function
  return () => {
    channel.unsubscribe();
  };
}
